import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  InnocodeButton,
  InnocodeDropdown,
  InnocodeInput,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { containsXss } from "../../../../utils/security/XssFilter";
import { ssoAdminApi } from "../../api";
import styles from "../../../../utils/modal/DetailModal.module.css";
import pageStyles from "../ServiceGroups.module.css";

const statusOptions = [
  { value: "ACTIVE", label: "활성" },
  { value: "INACTIVE", label: "비활성" },
];

const inputActionStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
};

const actionButtonStyle = {
  flexShrink: 0,
  minWidth: "96px",
};

const isInvalidText = (value) =>
  typeof value === "string" && value.trim() !== "" && containsXss(value);

const createDuplicateState = (groupName = "") => ({
  groupName: {
    checkedValue: groupName.trim(),
    duplicated: false,
  },
});

const formatGroupTypeLabel = (groupType) => {
  if (String(groupType || "").trim().toUpperCase() === "FEDERATION") {
    return "연합형";
  }

  return "내부형";
};

const formatRelationLabel = (detail) => {
  const relation = String(
    detail?.relation || detail?.relationType || detail?.myRelation || detail?.role || ""
  )
    .trim()
    .toUpperCase();

  if (relation === "OWNER") {
    return "오너";
  }

  if (relation === "PARTICIPANT") {
    return "파트너";
  }

  return "-";
};

const formatDateTime = (value) => {
  if (value == null || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  const date = Number.isFinite(numericValue)
    ? new Date(numericValue)
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("ko-KR");
};

function ServiceGroupDetailModal({
  groupId,
  contextTenantId,
  contextTenantName,
  relationType,
  tenantOptions,
  onClose,
  onSaved,
}) {
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const initialLoadGroupIdRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    groupKey: "",
    groupName: "",
    status: "ACTIVE",
    description: "",
  });
  const [duplicateCheck, setDuplicateCheck] = useState(createDuplicateState());

  const normalizedGroupId =
    typeof groupId === "object" && groupId !== null
      ? groupId.groupId || groupId.id || ""
      : groupId;
  const normalizedContextTenantId =
    typeof contextTenantId === "object" && contextTenantId !== null
      ? contextTenantId.tenantId || contextTenantId.id || ""
      : contextTenantId || "";
  const normalizedRelationType = String(relationType || "").trim().toUpperCase();

  const loadDetail = useCallback(async () => {
    if (!normalizedGroupId) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "상세 조회 실패",
        message: "서비스 그룹 ID가 올바르지 않습니다.",
        confirmText: "확인",
        onConfirm: onClose,
      });
      return;
    }

    showLoading();

    try {
      const result = await ssoAdminApi.fetchServiceGroupDetail(normalizedGroupId, {
        ...(normalizedContextTenantId
          ? { contextTenantId: normalizedContextTenantId }
          : {}),
        ...(normalizedRelationType ? { relationType: normalizedRelationType } : {}),
      });

      const normalizedDetail = {
        ...result,
        groupId: result?.groupId || normalizedGroupId,
        groupKey: result?.groupKey || "",
        groupName: result?.groupName || "",
        ownerTenantId: result?.ownerTenantId || "",
        ownerTenantName: result?.ownerTenantName || "",
        groupType: result?.groupType || "",
        status: result?.status || "ACTIVE",
        description: result?.description || result?.groupDescription || "",
        editable: Boolean(result?.editable),
        deletable: Boolean(result?.deletable),
        relation:
          result?.relationType ||
          result?.relation ||
          result?.myRelation ||
          result?.role ||
          normalizedRelationType,
        contextTenantId: result?.contextTenantId || normalizedContextTenantId,
        contextTenantName: result?.contextTenantName || contextTenantName || "",
        createdAt: result?.createdAt || result?.createdDate || "",
        updatedAt: result?.updatedAt || result?.updatedDate || "",
      };

      setDetail(normalizedDetail);
      setForm({
        groupKey: normalizedDetail.groupKey,
        groupName: normalizedDetail.groupName,
        status: normalizedDetail.status,
        description: normalizedDetail.description,
      });
      setDuplicateCheck(createDuplicateState(normalizedDetail.groupName));
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "상세 조회 실패",
        message:
          error?.response?.data?.message ||
          "서비스 그룹 상세 정보를 불러오지 못했습니다.",
        confirmText: "확인",
        onConfirm: onClose,
      });
    } finally {
      hideLoading();
    }
  }, [
    contextTenantName,
    hideLoading,
    normalizedContextTenantId,
    normalizedGroupId,
    normalizedRelationType,
    onClose,
    showDialog,
    showLoading,
  ]);

  useEffect(() => {
    const loadKey = normalizedGroupId == null ? "" : String(normalizedGroupId);
    if (initialLoadGroupIdRef.current === loadKey) {
      return;
    }

    initialLoadGroupIdRef.current = loadKey;
    loadDetail();
  }, [loadDetail, normalizedGroupId]);

  const ownerTenantLabel = useMemo(
    () =>
      detail?.ownerTenantName ||
      tenantOptions.find((option) => option.value === detail?.ownerTenantId)?.label ||
      detail?.ownerTenantId ||
      "-",
    [detail?.ownerTenantId, detail?.ownerTenantName, tenantOptions]
  );

  const handleChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "groupName") {
      setDuplicateCheck({
        groupName: {
          checkedValue: "",
          duplicated: null,
        },
      });
    }
  };

  const runGroupNameDuplicateCheck = async (options = {}) => {
    const { silent = false } = options;
    const value = form.groupName.trim();

    if (!silent && !value) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "그룹명을 입력해 주세요.",
        confirmText: "확인",
      });
      return { success: false, duplicated: null, value };
    }

    if (!silent && isInvalidText(value)) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "사용할 수 없는 문자가 포함되어 있습니다.",
        confirmText: "확인",
      });
      return { success: false, duplicated: null, value };
    }

    showLoading();

    try {
      const result = await ssoAdminApi.checkServiceGroupDuplicate({
        groupName: value,
        excludeGroupId: normalizedGroupId,
      });
      const duplicated = Boolean(
        result?.groupNameDuplicated ?? result?.duplicated
      );

      setDuplicateCheck({
        groupName: {
          checkedValue: value,
          duplicated,
        },
      });

      if (!silent) {
        showDialog({
          iconType: duplicated ? "warning" : "check",
          type: duplicated ? "error" : "success",
          title: duplicated ? "그룹명 중복" : "사용 가능",
          message: duplicated
            ? "이미 사용 중인 그룹명입니다."
            : "사용 가능한 그룹명입니다.",
          confirmText: "확인",
        });
      }

      return { success: true, duplicated, value };
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "그룹명 중복 확인 실패",
        message:
          error?.response?.data?.message ||
          "그룹명 중복 확인 중 오류가 발생했습니다.",
        confirmText: "확인",
      });
      return { success: false, duplicated: null, value };
    } finally {
      hideLoading();
    }
  };

  const validateBeforeUpdate = () => {
    if (!form.groupName.trim()) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 확인",
        message: "그룹명을 입력해 주세요.",
        confirmText: "확인",
      });
      return false;
    }

    if (isInvalidText(form.groupName) || isInvalidText(form.description)) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "입력 오류",
        message: "사용할 수 없는 문자가 포함되어 있습니다.",
        confirmText: "확인",
      });
      return false;
    }

    return true;
  };

  const handleUpdate = async () => {
    if (!detail?.editable) {
      return;
    }

    if (!validateBeforeUpdate()) {
      return;
    }

    const groupNameCheck = await runGroupNameDuplicateCheck({ silent: true });
    if (!groupNameCheck.success) {
      return;
    }

    if (groupNameCheck.duplicated) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "그룹명 중복",
        message: "이미 사용 중인 그룹명입니다. 다른 그룹명을 입력해 주세요.",
        confirmText: "확인",
      });
      return;
    }

    showLoading();

    try {
      await ssoAdminApi.updateServiceGroup(normalizedGroupId, {
        groupName: form.groupName.trim(),
        status: form.status,
        description: form.description.trim(),
      });
      await loadDetail();
      onSaved();
      showDialog({
        iconType: "check",
        type: "success",
        title: "수정 완료",
        message: "서비스 그룹 정보를 수정했습니다.",
        confirmText: "확인",
      });
    } catch (error) {
      showDialog({
        iconType: "warning",
        type: "error",
        title: "수정 실패",
        message:
          error?.response?.data?.message ||
          "서비스 그룹 수정 중 오류가 발생했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  };

  const handleDelete = () => {
    if (!detail?.deletable) {
      return;
    }

    showDialog({
      iconType: "warning",
      type: "delete",
      title: "그룹 삭제",
      message: "해당 서비스 그룹을 삭제하시겠습니까?",
      confirmText: "삭제",
      cancelText: "취소",
      onConfirm: async () => {
        showLoading();

        try {
          await ssoAdminApi.deleteServiceGroup(normalizedGroupId);
          onSaved();
          onClose();
          showDialog({
            iconType: "check",
            type: "success",
            title: "삭제 완료",
            message: "서비스 그룹을 삭제했습니다.",
            confirmText: "확인",
          });
        } catch (error) {
          showDialog({
            iconType: "warning",
            type: "error",
            title: "삭제 실패",
            message:
              error?.response?.data?.message ||
              "서비스 그룹 삭제 중 오류가 발생했습니다.",
            confirmText: "확인",
          });
        } finally {
          hideLoading();
        }
      },
    });
  };

  return (
    <div>
      {detail ? (
        <>
          <ul className={styles.layer__listWrap}>
            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>그룹 키</p>
              <input
                className={styles.layer__listInput}
                type="text"
                value={form.groupKey}
                readOnly={true}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>그룹명</p>
              <div style={inputActionStyle}>
                <input
                  className={styles.layer__listInput}
                  type="text"
                  value={form.groupName}
                  onChange={(event) => handleChange("groupName", event.target.value)}
                  placeholder="예: 코어 서비스 그룹"
                  readOnly={!detail.editable}
                  disabled={!detail.editable}
                />
                {detail.editable ? (
                  <InnocodeButton
                    size="sm"
                    theme="line"
                    designType="primary"
                    style={actionButtonStyle}
                    onClick={() => runGroupNameDuplicateCheck()}
                  >
                    중복 확인
                  </InnocodeButton>
                ) : null}
              </div>
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>소유 테넌트</p>
              <input
                className={styles.layer__listInput}
                type="text"
                value={ownerTenantLabel}
                readOnly={true}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>내 테넌트</p>
              <input
                className={styles.layer__listInput}
                type="text"
                value={detail.contextTenantName || detail.contextTenantId || "-"}
                readOnly={true}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>관계</p>
              <input
                className={styles.layer__listInput}
                type="text"
                value={formatRelationLabel(detail)}
                readOnly={true}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>유형</p>
              <input
                className={styles.layer__listInput}
                type="text"
                value={formatGroupTypeLabel(detail.groupType)}
                readOnly={true}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>상태</p>
              <InnocodeDropdown
                value={form.status}
                options={statusOptions}
                onChange={(value) => handleChange("status", value || "ACTIVE")}
                disabled={!detail.editable}
              />
            </li>

            <li className={styles.layer__list}>
              <p className={styles.layer__listTit}>설명</p>
              <InnocodeInput
                multiline={true}
                rows={4}
                value={form.description}
                onChange={(event) => handleChange("description", event.target.value)}
                readOnly={!detail.editable}
                disabled={!detail.editable}
              />
            </li>
          </ul>

          <div className={styles.layer__sensorBtnBox}>
            {detail.deletable ? (
              <InnocodeButton
              className={styles.layer__actionBtn}
                backgroundColor="var(--status-negative, #f83a3a)"
                onClick={handleDelete}
                fullWidth={true}
              >
                삭제
              </InnocodeButton>
            ) : null}
            {detail.editable ? (
              <InnocodeButton 
                className={styles.layer__actionBtn}
                onClick={handleUpdate}
                fullWidth={true}
              >
                저장</InnocodeButton>
            ) : null}
          </div>
        </>
      ) : (
        <div className={pageStyles.emptyBox}>상세 정보를 불러오는 중입니다.</div>
      )}
    </div>
  );
}

export default ServiceGroupDetailModal;
